import { create } from 'zustand';

type Theme = 'light' | 'dark' | 'system';

interface ThemeState {
  theme: Theme;
  setTheme: (theme: Theme) => void;
}

function getSystemTheme(): 'light' | 'dark' {
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

function applyTheme(theme: Theme) {
  const resolved = theme === 'system' ? getSystemTheme() : theme;
  document.documentElement.classList.remove('light', 'dark');
  document.documentElement.classList.add(resolved);
}

const THEME_KEY = 'tally-theme';

function readStored(): Theme {
  try {
    // 'tripper-theme' is the key from before the rename.
    return ((localStorage.getItem(THEME_KEY) ?? localStorage.getItem('tripper-theme')) as Theme) || 'system';
  } catch {
    return 'system';
  }
}

const stored = readStored();
applyTheme(stored);

// "System" follows the OS live, e.g. when the phone switches to dark mode at sunset.
try {
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    if (useThemeStore.getState().theme === 'system') applyTheme('system');
  });
} catch {
  // Older browsers without addEventListener on MediaQueryList: the theme updates on reload.
}

export const useThemeStore = create<ThemeState>((set) => ({
  theme: stored,
  setTheme: (theme) => {
    try {
      localStorage.setItem(THEME_KEY, theme);
    } catch {
      // Private mode or blocked storage: the choice lasts for this visit only.
    }
    applyTheme(theme);
    set({ theme });
  },
}));
